import * as logger from 'loglevel';
import axios from 'axios';
import { getValue } from '../utils';
import { getHashParams, isSSR } from '@front10/utils';

/**
 * When converting errors could be issues with circular references
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value
 */
const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    // eslint-disable-next-line consistent-return
    return value;
  };
};

const getErrOrigin = () => {
  if (isSSR()) {
    return 'SSR';
  }
  return window.location.href;
};

const sendErrorToCollectorServer = (error, integrationSettings) => {
  return new Promise((resolve, reject) => {
    if (
      integrationSettings &&
      integrationSettings.logsCollector &&
      integrationSettings.logsCollector.active
    ) {
      const { logsCollector } = integrationSettings;
      const collectorData = {
        errMsg: error.message,
        errRawMsg: error.rawMessage,
        errOrigin: getErrOrigin(),
        cmpMeta: error.cmpMeta,
        pageContext: error.pageContext
      };
      if (error.request && error.config) {
        collectorData.reqInfo = {};
        collectorData.reqInfo.url = error.config.url;
        collectorData.reqInfo.query = error.config.data;
        collectorData.reqInfo.headers = error.config.headers;
      }
      if (error.response && error.response.data) {
        collectorData.resInfo = {};
        collectorData.resInfo.status = error.response.data.status;
        collectorData.resInfo.timestamp = error.response.data.timestamp;
        collectorData.resInfo.message = error.response.data.error;
      }
      if (!isSSR() && typeof navigator !== 'undefined' && navigator.connection) {
        collectorData.connection = {
          downlink: navigator.connection.downlink,
          effectiveType: navigator.connection.effectiveType,
          type: navigator.connection.type,
          rtt: navigator.connection.rtt
        };
      }
      axios({
        method: logsCollector.method,
        url: `${logsCollector.endPoint}`,
        headers: { ...logsCollector.headers },
        data: collectorData
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          logger.error(err);
          reject(err);
        });
    }
  });
};

const getSimplifiedPageContext = (pageContext) => {
  if (pageContext) {
    let userAgent = '';
    if (!isSSR() && typeof navigator !== 'undefined' && navigator.userAgent) {
      // eslint-disable-next-line prefer-destructuring
      userAgent = navigator.userAgent;
    }
    const simplifiedContext = {
      airlineIataCode: getValue(pageContext, 'airlineIataCode'),
      language: getValue(pageContext, 'language'),
      siteEdition: getValue(pageContext, 'siteEdition'),
      pageType: getValue(pageContext, 'pageType'),
      deviceCategory: getValue(pageContext, 'deviceCategory'),
      url: getValue(pageContext, 'url'),
      renderType: getValue(pageContext, 'renderType'),
      userAgent
    };
    return simplifiedContext;
  }
  return false;
};

/**
 * Decorate standar Error Object with custom annotations and dates
 * @param {*} error
 * @param {*} annotation
 * @param {*} cmpConfig
 */
const annotateAndLogError = (
  error,
  annotation,
  cmpConfig = undefined,
  traceMode = false,
  integrationSettings = undefined,
  cmpMeta = undefined,
  pageContext = undefined,
  allowLogDuplication = false
) => {
  const myError = Object.assign({}, error);
  if (!allowLogDuplication && !myError.middlemanCount) {
    myError.message = `${annotation}: ${error.message}`;
    myError.rawMessage = `${error.message}`;
    myError.stack = error.stack;
    if (!myError.tags) {
      myError.tags = [];
    }
    myError.date = new Date().toUTCString();
    myError.tags.push(annotation);
    myError.cmpConfig = cmpConfig || false;
    myError.cmpMeta = cmpMeta || false;
    if (cmpConfig && cmpConfig.pageContext) {
      myError.pageContext = getSimplifiedPageContext(cmpConfig.pageContext);
    } else {
      myError.pageContext = getSimplifiedPageContext(pageContext);
    }

    if (!myError.middlemanCount) {
      myError.middlemanCount = 0;
    }
    myError.middlemanCount += 1;
    if (myError.cmpConfig && myError.cmpConfig.envConfig) {
      myError.cmpConfig.envConfig = null;
      delete myError.cmpConfig.envConfig;
    }

    if (traceMode) {
      logger.info(`${annotation}:`, error);
      myError.traceMode = true;
    } else {
      let myIntegrationSettings = integrationSettings;
      if (!myIntegrationSettings && cmpConfig) {
        myIntegrationSettings = getValue(cmpConfig, 'containerConfig.settings.integrationSettings');
      }
      sendErrorToCollectorServer(myError, myIntegrationSettings).catch((err) => {
        logger.error(err);
      });
      logger.error(`${annotation}:`, error);
    }
    return myError;
  }
  return error;
};

/**
 * Returns a Standar Error object in string format
 * this help to avoid unnecesary extra calculation in React
 * also circular references and stuff like that
 * @param {*} error
 */
const getSanitizedError = (error) => {
  return JSON.stringify(error, getCircularReplacer());
};

/**
 * Check if there is #debugMe in the url
 * @param {*} forceDebug
 */
const isDebugModeActive = (forceDebug) => {
  if (forceDebug) {
    return true;
  }
  return false;
};

const isDebugModeVisible = () => {
  if (typeof window !== 'undefined') {
    const urlParsedHash = getHashParams();
    // eslint-disable-next-line no-prototype-builtins
    if (urlParsedHash.hasOwnProperty('debugMe')) {
      return true;
    }
    return false;
  }
  return false;
};

export { annotateAndLogError, getSanitizedError, isDebugModeActive, isDebugModeVisible };
